import React from 'react'
import styled from 'styled-components'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import {
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import richTextHtmlParser from '@jarvis.oddle/core/components/TextEditor/parser'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectSections } from 'src/redux/customisation'
import ResponsiveSection from 'src/components/core/ResponsiveSection'

const Section = styled(Div)`
  display: block;
  width: 100%;
`

const Container = styled(Div)<{
  backgroundColor: string
}>`
  background-color: ${(props) =>
    props.backgroundColor
      ? props.backgroundColor
      : props.theme.colorContainerColor};
  position: relative;
`

const ContentRow = styled(Flex)<{
  backgroundColor: string
}>`
  flex-direction: column;
  text-align: center;
  background-color: ${(props) => props.backgroundColor};
  ${breakpoint('desktop')`
    flex-direction: row;
  `}
`

const ContentBox = styled(Div)`
  display: block;
  width: 100%;
  padding: 15px 7px;
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.medium]} ${padding[paddingSizes.small]};
  `}
`

// In Chrome & IE, hr should be styled with background color
// but with vertical hr we use border-color to unifiy the behaviour
const ResponsiveDivider = styled.hr<{
  color: string
}>`
  border: 0;
  opacity: 0.5;
  background-color: ${(props) => props.color};
  height: 1px;
  width: auto;
  margin: 0 7px;
  ${breakpoint('desktop')`
    height: auto;
    width: 1px;
    background-color: initial;
    margin: ${margin[marginSizes.medium]} ${margin[marginSizes.small]};
    border-left: 1px solid ${(props) => props.color};
  `}
`

interface AnnouncementBannerSection {
  id: string
  name: string
  type: string
  settings: {
    bannerFontColor: string
    bannerBackgroundColor: string
  }
  blocks?: {
    [key: string]: {
      id: string
      name: string
      type: string
      settings: {
        headerText: string
        bodyText: string
      }
    }
  }
  blocksOrder?: string[]
  createAt: string
  updatedAt: string
}

interface AnnouncementBannerProps {
  id: string
}

interface GenerateBlocks {
  color: string
  blocksOrder: string[]
  blocks: {
    [key: string]: {
      id: string
      name: string
      settings: {
        bodyText: string
        headerText: string
      }
      type: string
    }
  }
}

const generateBlocks = ({
  color,
  blocksOrder = [],
  blocks
}: GenerateBlocks): JSX.Element[] => {
  const sections = []

  for (let i = 0; i < blocksOrder.length; i++) {
    if (i > 0) {
      // As divider
      sections.push(<ResponsiveDivider key={`d-${i}`} color={color} />)
    }

    const { settings } = blocks[blocksOrder[i]] || {}
    const { headerText, bodyText } = settings || {}
    sections.push(
      // As content
      <ContentBox key={`c-${i}`}>
        <H4 color={color} marginBottom={marginSizes.extraSmall} bold normalised>
          {headerText}
        </H4>
        <H4 normalised>
          {richTextHtmlParser(bodyText, { normalised: true, color })}
        </H4>
      </ContentBox>
    )
  }

  return sections
}

const AnnouncementBanner = ({ id }: AnnouncementBannerProps): JSX.Element => {
  const { blocks, blocksOrder, settings } = useAppSelector(selectSections)[
    id
  ] as AnnouncementBannerSection
  const { bannerFontColor, bannerBackgroundColor } = settings || {}
  return (
    <Container backgroundColor={bannerBackgroundColor}>
      <Section>
        <ResponsiveSection.Container height='100%'>
          <ContentRow>
            {generateBlocks({ blocks, blocksOrder, color: bannerFontColor })}
          </ContentRow>
        </ResponsiveSection.Container>
      </Section>
    </Container>
  )
}

export default AnnouncementBanner
